import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, Globe, Sparkles } from 'lucide-react';
import { fetchOpenAIChatCompletion } from '../lib/openaiClient';
import { supabase } from '../lib/supabase';
import './StudyPlan.css';

const BRAND_LOGO_RULES: Array<{ re: RegExp; domain: string }> = [
  { re: /magoosh/i, domain: 'magoosh.com' },
  { re: /manhattan/i, domain: 'manhattanprep.com' },
  { re: /kaplan/i, domain: 'kaplan.com' },
  { re: /princeton/i, domain: 'princetonreview.com' },
  { re: /gmac|official\s+gmat|mba\.com/i, domain: 'gmac.com' },
  { re: /\bets\b|official\s+gre/i, domain: 'ets.org' },
  { re: /khan\s*academy/i, domain: 'khanacademy.org' },
  { re: /target\s*test\s*prep|\bttp\b/i, domain: 'targettestprep.com' },
  { re: /veritas/i, domain: 'veritasprep.com' },
  { re: /powerscore/i, domain: 'powerscore.com' }
];

function clearbitLogoUrl(domain: string) {
  return `https://logo.clearbit.com/${domain}`;
}

function domainForResourceText(text: string): string | null {
  for (const { re, domain } of BRAND_LOGO_RULES) {
    if (re.test(text)) return domain;
  }
  return null;
}

/** Strip a leading emoji / pictograph cluster (best-effort for model output). */
function stripLeadingEmoji(text: string): string {
  let t = text.trimStart();
  const isEmojiCp = (cp: number) =>
    (cp >= 0x1f300 && cp <= 0x1faf6) ||
    (cp >= 0x2600 && cp <= 0x27bf) ||
    (cp >= 0x1f600 && cp <= 0x1f64f) ||
    cp === 0x24c2 ||
    cp === 0x2705;

  for (let guard = 0; guard < 8; guard += 1) {
    const cp = t.codePointAt(0);
    if (cp === undefined || !isEmojiCp(cp)) break;
    t = t.slice(cp >= 0x10000 ? 2 : 1);
    t = t.replace(/^\uFE0F/, '');
    if (t.startsWith('\u200D')) {
      t = t.slice(1);
      continue;
    }
    t = t.replace(/^[\s,.:;·\-–—]+/, '');
    break;
  }
  return t.trimStart();
}

/**
 * Pre-compute structural numbers we want the model to *use*, not invent.
 * Grounding cheap models in exact math is the highest-ROI quality lift.
 */
function computeStudyPlanContext(fd: StudyPlanData, scoreEnabled: ScoreEnabledState) {
  const totalWeeks = Math.max(1, Math.round(fd.studyTimeframe * 4.33));
  const totalHours = totalWeeks * fd.hoursPerWeek;

  const cfg = SCORE_CONFIG[fd.testType];
  const sectionRange = cfg.section.max - cfg.section.min || 1;

  const currentQ = scoreEnabled.currentQuantScore ? fd.currentQuantScore : undefined;
  const currentV = scoreEnabled.currentVerbalScore ? fd.currentVerbalScore : undefined;
  const targetQ = scoreEnabled.targetQuantScore ? fd.targetQuantScore : undefined;
  const targetV = scoreEnabled.targetVerbalScore ? fd.targetVerbalScore : undefined;

  const quantGap =
    typeof currentQ === 'number' && typeof targetQ === 'number'
      ? Math.max(0, targetQ - currentQ)
      : null;
  const verbalGap =
    typeof currentV === 'number' && typeof targetV === 'number'
      ? Math.max(0, targetV - currentV)
      : null;

  let quantWeight = 0.5;
  let verbalWeight = 0.5;
  if (quantGap !== null && verbalGap !== null) {
    const totalGapNorm = quantGap / sectionRange + verbalGap / sectionRange;
    if (totalGapNorm > 0) {
      quantWeight = quantGap / sectionRange / totalGapNorm;
      verbalWeight = verbalGap / sectionRange / totalGapNorm;
    }
  }

  const totalGap =
    scoreEnabled.currentScore && typeof fd.currentScore === 'number'
      ? Math.max(0, fd.targetScore - fd.currentScore)
      : null;

  // Practice test cadence anchored to phases.
  const practiceTestWeeks: number[] = [];
  if (totalWeeks >= 2) practiceTestWeeks.push(1);
  if (totalWeeks >= 6) practiceTestWeeks.push(Math.round(totalWeeks * 0.5));
  if (totalWeeks >= 4) practiceTestWeeks.push(Math.max(1, totalWeeks - 1));
  if (totalWeeks >= 8) practiceTestWeeks.push(Math.max(1, totalWeeks - 3));
  const practiceTestWeeksUnique = Array.from(new Set(practiceTestWeeks)).sort(
    (a, b) => a - b
  );

  // Phase plan: distribute weeks across 5 fixed phases.
  const phasePcts = [0.10, 0.25, 0.30, 0.20, 0.15] as const;
  const phaseNames = [
    'Diagnostic & Foundations',
    'Concept Mastery',
    'Mixed Practice & Strategy',
    'Test Simulation & Timing',
    'Final Polish & Confidence'
  ] as const;
  let allocated = 0;
  const phasePlan = phasePcts.map((p, i) => {
    const isLast = i === phasePcts.length - 1;
    const len = isLast
      ? Math.max(1, totalWeeks - allocated)
      : Math.max(1, Math.round(totalWeeks * p));
    const startWeek = allocated + 1;
    allocated += len;
    return { name: phaseNames[i], startWeek, endWeek: Math.min(totalWeeks, allocated), weeks: len };
  });

  // Realism check: rough heuristic only.
  const baselineHoursPerTotalPoint = fd.testType === 'GRE' ? 4 : 0.7;
  const recommendedHours =
    totalGap !== null ? Math.round(totalGap * baselineHoursPerTotalPoint) : null;
  const isAggressive =
    recommendedHours !== null && recommendedHours > totalHours * 1.15;

  return {
    totalWeeks,
    totalHours,
    quantGap,
    verbalGap,
    quantWeight: Math.round(quantWeight * 100) / 100,
    verbalWeight: Math.round(verbalWeight * 100) / 100,
    totalGap,
    recommendedHours,
    isAggressive,
    practiceTestWeeks: practiceTestWeeksUnique,
    phasePlan
  };
}

/**
 * Best-effort fixer: keeps whatever the model returned but fills/normalizes
 * the structure so the UI never breaks.
 */
function validateAndRepairStudyPlan(
  raw: unknown,
  ctx: ReturnType<typeof computeStudyPlanContext>
): GeneratedStudyPlan {
  const safe = (raw && typeof raw === 'object' ? raw : {}) as Partial<GeneratedStudyPlan>;

  const overview = typeof safe.overview === 'string' ? safe.overview : 'Personalized study plan generated.';

  const incomingWeekly = Array.isArray(safe.weeklyPlan) ? safe.weeklyPlan : [];
  const repairedWeekly = Array.from({ length: ctx.totalWeeks }).map((_, idx) => {
    const weekNumber = idx + 1;
    const found = incomingWeekly.find(
      (w) => w && typeof w === 'object' && (w as { week?: number }).week === weekNumber
    ) as GeneratedStudyPlan['weeklyPlan'][number] | undefined;
    const phase = ctx.phasePlan.find((p) => weekNumber >= p.startWeek && weekNumber <= p.endWeek);
    return {
      week: weekNumber,
      focus: found?.focus || (phase ? phase.name : 'Targeted study'),
      tasks: Array.isArray(found?.tasks) && found!.tasks.length > 0
        ? found!.tasks.map((t) => String(t))
        : [
            `Focus area: ${phase ? phase.name : 'Core concepts'}`,
            `Hour budget: ~${Math.max(1, Math.round(ctx.totalHours / ctx.totalWeeks))} hours`,
            'Review mistakes from previous week and log error patterns'
          ],
      practiceTests: typeof found?.practiceTests === 'string' ? found!.practiceTests : undefined,
      resources: Array.isArray(found?.resources) ? found!.resources.map((r) => String(r)) : []
    };
  });

  const repairedPracticeTests = ctx.practiceTestWeeks.map((week) => {
    const incoming = Array.isArray(safe.practiceTestSchedule)
      ? safe.practiceTestSchedule.find(
          (t) => t && typeof t === 'object' && (t as { week?: number }).week === week
        )
      : undefined;
    return {
      week,
      testType: (incoming as { testType?: string })?.testType || 'Full-length practice test',
      focus:
        (incoming as { focus?: string })?.focus ||
        (week === 1
          ? 'Diagnostic baseline — identify section strengths and gaps'
          : week === ctx.totalWeeks - 1
            ? 'Final timed simulation under real test conditions'
            : 'Track progress and adjust the remaining plan')
    };
  });

  return {
    overview,
    weeklyPlan: repairedWeekly,
    practiceTestSchedule: repairedPracticeTests,
    studyStrategies: Array.isArray(safe.studyStrategies)
      ? safe.studyStrategies.map((s) => String(s))
      : [],
    resourceRecommendations: Array.isArray(safe.resourceRecommendations)
      ? safe.resourceRecommendations.map((c) => ({
          category: String((c as { category?: string })?.category || 'Resources'),
          items: Array.isArray((c as { items?: unknown[] })?.items)
            ? (c as { items: unknown[] }).items.map((i) => String(i))
            : []
        }))
      : [],
    milestones: Array.isArray(safe.milestones)
      ? safe.milestones.map((m) => ({
          week: typeof (m as { week?: number })?.week === 'number' ? (m as { week: number }).week : 1,
          milestone: String((m as { milestone?: string })?.milestone || 'Milestone'),
          description: String((m as { description?: string })?.description || '')
        }))
      : []
  };
}

function StudyPlanResourceLeadIcon({ text, category }: { text: string; category: string }) {
  const domain = domainForResourceText(text);
  const [logoFailed, setLogoFailed] = useState(false);
  const cat = category.toLowerCase();
  const useGlobe =
    /online|video|digital|course|app|platform|website|web|youtube|podcast/i.test(cat) ||
    /youtube|podcast|website|\.com\/|http/i.test(text);
  const FallbackIcon = useGlobe ? Globe : BookOpen;

  if (domain && !logoFailed) {
    return (
      <img
        src={clearbitLogoUrl(domain)}
        alt=""
        className="study-plan-brand-logo"
        onError={() => setLogoFailed(true)}
      />
    );
  }
  return <FallbackIcon className="study-plan-li-svg" aria-hidden focusable="false" />;
}

interface StudyPlanData {
  testType: 'GMAT_LEGACY' | 'GMAT_FOCUS' | 'GRE';
  currentScore?: number;
  currentQuantScore?: number;
  currentVerbalScore?: number;
  targetScore: number;
  targetQuantScore?: number;
  targetVerbalScore?: number;
  testDate?: string;
  studyTimeframe: number;
  hoursPerWeek: number;
}

interface GeneratedStudyPlan {
  overview: string;
  weeklyPlan: {
    week: number;
    focus: string;
    tasks: string[];
    practiceTests?: string;
    resources: string[];
  }[];
  practiceTestSchedule: {
    week: number;
    testType: string;
    focus: string;
  }[];
  studyStrategies: string[];
  resourceRecommendations: {
    category: string;
    items: string[];
  }[];
  milestones: {
    week: number;
    milestone: string;
    description: string;
  }[];
}

type OptionalScoreField =
  | 'currentScore'
  | 'currentQuantScore'
  | 'currentVerbalScore'
  | 'targetQuantScore'
  | 'targetVerbalScore';

type ScoreEnabledState = Record<OptionalScoreField, boolean>;

const SCORE_CONFIG = {
  GMAT_LEGACY: {
    total: { min: 200, max: 800, defaultValue: 650, step: 10 },
    section: { min: 6, max: 51, defaultValue: 35, step: 1 },
    targetTotalDefault: 700
  },
  GMAT_FOCUS: {
    total: { min: 205, max: 805, defaultValue: 655, step: 10 },
    section: { min: 60, max: 90, defaultValue: 75, step: 1 },
    targetTotalDefault: 695
  },
  GRE: {
    total: { min: 260, max: 340, defaultValue: 310, step: 1 },
    section: { min: 130, max: 170, defaultValue: 155, step: 1 },
    targetTotalDefault: 320
  }
} as const;

const TEST_TYPE_LABEL: Record<StudyPlanData['testType'], string> = {
  GMAT_LEGACY: 'GMAT Legacy',
  GMAT_FOCUS: 'GMAT Focus Edition',
  GRE: 'GRE'
};

/** Discrete horizons keep prompts and weekly breakdown math simpler */
const STUDY_TIMEFRAME_VALUES = [2, 3, 4, 6, 9] as const;
const STUDY_TIMEFRAME_OPTIONS = STUDY_TIMEFRAME_VALUES.map((value) => ({
  value,
  label: String(value)
}));

function nearestStudyTimeframeMonths(months: number): number {
  const allowed = [...STUDY_TIMEFRAME_VALUES];
  return allowed.reduce((best, v) =>
    Math.abs(v - months) < Math.abs(best - months) ? v : best
  );
}

const HOURS_PER_WEEK_VALUES = [5, 10, 15, 20, 25] as const;
const HOURS_PER_WEEK_OPTIONS = HOURS_PER_WEEK_VALUES.map((value) => ({
  value,
  label: value === 25 ? '25+' : String(value)
}));

function studyTimeframeAriaLabel(months: number): string {
  return `${months} ${months === 1 ? 'month' : 'months'}`;
}

function hoursPerWeekAriaLabel(hours: number): string {
  if (hours === 25) return '25 or more hours per week';
  return `${hours} hours per week`;
}

function normalizeStudyPlanFormData(fd: StudyPlanData): StudyPlanData {
  const allowedMonths = new Set<number>([...STUDY_TIMEFRAME_VALUES]);
  let studyTimeframe = fd.studyTimeframe;
  if (typeof studyTimeframe !== 'number' || Number.isNaN(studyTimeframe)) {
    studyTimeframe = 9;
  } else if (!allowedMonths.has(studyTimeframe)) {
    studyTimeframe = nearestStudyTimeframeMonths(studyTimeframe);
  }
  const allowedHours = new Set<number>([...HOURS_PER_WEEK_VALUES]);
  let hoursPerWeek = fd.hoursPerWeek;
  if (typeof hoursPerWeek !== 'number' || Number.isNaN(hoursPerWeek) || !allowedHours.has(hoursPerWeek)) {
    hoursPerWeek = 10;
  }
  return { ...fd, studyTimeframe, hoursPerWeek };
}

const StudyPlan: React.FC = () => {
  const [formData, setFormData] = useState<StudyPlanData>({
    testType: 'GMAT_LEGACY',
    targetScore: 700,
    studyTimeframe: 9,
    hoursPerWeek: 10
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [studyPlan, setStudyPlan] = useState<GeneratedStudyPlan | null>(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [completedTasks, setCompletedTasks] = useState<Set<string>>(new Set());
  const [scoreEnabled, setScoreEnabled] = useState<ScoreEnabledState>({
    currentScore: true,
    currentQuantScore: true,
    currentVerbalScore: true,
    targetQuantScore: true,
    targetVerbalScore: true
  });

  const STUDY_PLAN_STORAGE_KEY = 'deferredly.studyPlan.v1';

  const persistToLocalStorage = useCallback(() => {
    try {
      localStorage.setItem(
        STUDY_PLAN_STORAGE_KEY,
        JSON.stringify({
          formData,
          studyPlan,
          completedTasks: Array.from(completedTasks),
          scoreEnabled
        })
      );
    } catch {
      /* ignore */
    }
  }, [completedTasks, formData, scoreEnabled, studyPlan]);

  // Restore the most recent generated study plan after navigation.
  // (Purely a client-side temporary cache.)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STUDY_PLAN_STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw) as {
        formData?: StudyPlanData;
        studyPlan?: GeneratedStudyPlan | null;
        completedTasks?: string[];
        scoreEnabled?: Partial<ScoreEnabledState>;
      };

      if (parsed?.formData) setFormData(normalizeStudyPlanFormData(parsed.formData));
      if (parsed?.studyPlan) setStudyPlan(parsed.studyPlan);
      setCompletedTasks(new Set(parsed?.completedTasks || []));
      if (parsed?.scoreEnabled) {
        setScoreEnabled((prev) => ({ ...prev, ...parsed.scoreEnabled }));
      }
    } catch (err) {
      console.warn('Failed to restore cached study plan:', err);
    }
    // Intentionally only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (field: keyof StudyPlanData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTestTypeChange = (testType: StudyPlanData['testType']) => {
    const testConfig = SCORE_CONFIG[testType];
    setFormData((prev) => {
      const targetScore =
        prev.testType === testType
          ? prev.targetScore
          : testConfig.targetTotalDefault;
      return {
        ...prev,
        testType,
        targetScore: Math.min(testConfig.total.max, Math.max(testConfig.total.min, targetScore))
      };
    });
  };

  const handleScoreEnabledChange = (field: OptionalScoreField, enabled: boolean) => {
    setScoreEnabled((prev) => ({ ...prev, [field]: enabled }));
  };

  const getScoreValue = (
    field: keyof StudyPlanData,
    scoreType: 'total' | 'section'
  ) => {
    const config = SCORE_CONFIG[formData.testType][scoreType];
    const current = formData[field];
    if (typeof current === 'number') {
      return Math.min(config.max, Math.max(config.min, current));
    }
    return config.defaultValue;
  };

  const saveStudyPlan = async () => {
    persistToLocalStorage();
    if (!studyPlan) {
      setMessage(
        'Your planner inputs were saved on this device. Generate a study plan to save your full roadmap to your account (where available).'
      );
      setMessageType('success');
      return;
    }

    try {
      setIsSaving(true);
      setMessage('');

      const optionalScore = (field: OptionalScoreField) =>
        scoreEnabled[field] ? formData[field] : undefined;

      const studyPlanData = {
        test_type: formData.testType,
        current_score: optionalScore('currentScore'),
        current_quant_score: optionalScore('currentQuantScore'),
        current_verbal_score: optionalScore('currentVerbalScore'),
        target_score: formData.targetScore,
        target_quant_score: optionalScore('targetQuantScore'),
        target_verbal_score: optionalScore('targetVerbalScore'),
        test_date: formData.testDate,
        study_timeframe_months: formData.studyTimeframe,
        hours_per_week: formData.hoursPerWeek,
        generated_plan: JSON.stringify(studyPlan),
        completed_tasks: Array.from(completedTasks)
      };

      const { error } = await supabase
        .from('study_plans')
        .upsert([studyPlanData], { onConflict: 'id' });

      if (error) throw error;

      setMessage('Study plan saved successfully!');
      setMessageType('success');
    } catch (error) {
      console.error('Error saving study plan:', error);
      setMessage('Error saving study plan. Please try again.');
      setMessageType('error');
    } finally {
      setIsSaving(false);
    }
  };

  const generateStudyPlan = async () => {
    try {
      setIsGenerating(true);
      setMessage('');

      const optionalScore = (field: OptionalScoreField) =>
        scoreEnabled[field] ? formData[field] : undefined;
      const scoreLabels =
        formData.testType === 'GMAT_LEGACY'
          ? '(GMAT Legacy: total 200–800; Quant/Verbal 6–51)'
          : formData.testType === 'GMAT_FOCUS'
            ? '(GMAT Focus: total 205–805; Quant/Verbal 60–90)'
            : '(GRE: total 260–340; Quant/Verbal 130–170)';

      const ctx = computeStudyPlanContext(formData, scoreEnabled);
      const phaseSummary = ctx.phasePlan
        .map((p) => `Phase: ${p.name} — Weeks ${p.startWeek}–${p.endWeek} (${p.weeks} weeks)`)
        .join('\n');

      const formText = `
Test Type: ${TEST_TYPE_LABEL[formData.testType]}
Current Score: ${optionalScore('currentScore') ?? 'Not provided'}
Current Quant: ${optionalScore('currentQuantScore') ?? 'Not provided'}
Current Verbal: ${optionalScore('currentVerbalScore') ?? 'Not provided'}
Target Score: ${formData.targetScore}
Target Quant: ${optionalScore('targetQuantScore') ?? 'Not provided'}
Target Verbal: ${optionalScore('targetVerbalScore') ?? 'Not provided'}
${scoreLabels}
Test Date: ${formData.testDate || 'Not specified'}
Study Timeframe: ${formData.studyTimeframe} months
Hours per Week: ${formData.hoursPerWeek}

PRECOMPUTED STRUCTURE (use these exact numbers, do not invent):
- Total weeks in plan: ${ctx.totalWeeks}
- Total study hours available: ${ctx.totalHours}
- Quant time weight: ${Math.round(ctx.quantWeight * 100)}%
- Verbal time weight: ${Math.round(ctx.verbalWeight * 100)}%
- Practice test weeks (must match exactly): ${ctx.practiceTestWeeks.join(', ')}
- Phases (use these names and week ranges exactly):
${phaseSummary}
- Realism note: ${ctx.isAggressive ? 'AGGRESSIVE — call this out and recommend +hours/week.' : 'Reasonable for the timeline.'}
      `.trim();

      const systemPrompt = `You are an expert GMAT/GRE tutor producing a premium, realistic, personalized study plan.

You MUST return valid JSON only, matching this schema:

{
  "overview": "2-3 sentences explaining the strategy, citing total weeks, total hours, and section weighting.",
  "weeklyPlan": [
    {
      "week": 1,
      "focus": "Phase name and primary topic(s) for this week",
      "tasks": [
        "Topic-tagged task with hours, e.g. 'Quant: Number Properties — 4 hours'",
        "Topic-tagged task with hours, e.g. 'Verbal: Critical Reasoning — 3 hours'",
        "Review session: log error patterns and re-do missed questions"
      ],
      "practiceTests": "Only set on practice test weeks. Otherwise omit.",
      "resources": ["Specific book chapter or course module", "Specific online resource"]
    }
  ],
  "practiceTestSchedule": [
    { "week": 1, "testType": "Diagnostic", "focus": "Establish baseline and identify weak areas" }
  ],
  "studyStrategies": ["3-6 concrete strategies tailored to the user's gap and weighting."],
  "resourceRecommendations": [
    { "category": "Books", "items": ["Specific named books"] },
    { "category": "Online Resources", "items": ["Specific named courses/sites"] }
  ],
  "milestones": [
    { "week": 4, "milestone": "Concrete checkpoint", "description": "How to know you're on track" }
  ]
}

HARD RULES:
1. Output exactly ${ctx.totalWeeks} entries in "weeklyPlan", numbered 1..${ctx.totalWeeks}.
2. Each week's "focus" must reference the phase that contains that week.
3. Each week's "tasks" array must include 3-5 items. Every Quant/Verbal task MUST be topic-tagged (e.g. "Quant: Number Properties", "Verbal: Reading Comprehension") and include an hour estimate.
4. The hours across each week's tasks should sum to approximately ${formData.hoursPerWeek} hours.
5. Allocate Quant vs Verbal study time roughly to ${Math.round(ctx.quantWeight * 100)}% / ${Math.round(ctx.verbalWeight * 100)}% across the plan; weight the weaker section heavier in early phases.
6. "practiceTestSchedule" must contain exactly these weeks: ${ctx.practiceTestWeeks.join(', ')}.
7. The first practice test (week 1) must be labeled as Diagnostic.
8. The last 2 phases must include at least 2 timed full-length practice tests.
9. Use ONLY plain text in JSON values. NO emojis, NO markdown, NO unicode symbols.
10. Use realistic, named resources only (e.g. "Official Guide for GMAT Review", "Manhattan Prep Strategy Guides", "GMAT Club", "ETS PowerPrep", "Magoosh", "Khan Academy"). Never invent fake books or courses.
11. If "Realism note" says AGGRESSIVE, the overview must clearly mention the timeline is tight and recommend adding hours/week.
12. Numbers in JSON must be JSON numbers (not strings).
`;

      const planText = await fetchOpenAIChatCompletion({
        model: 'gpt-3.5-turbo',
        response_format: { type: 'json_object' },
        messages: [
          { role: 'system', content: systemPrompt },
          {
            role: 'user',
            content: `Create the personalized ${TEST_TYPE_LABEL[formData.testType]} study plan as JSON.\n\n${formText}`
          }
        ],
        max_tokens: 2200,
        temperature: 0.3
      });

      let parsedPlan: unknown = null;
      try {
        parsedPlan = JSON.parse(planText);
      } catch {
        // Try to extract a JSON object from a noisy response (defensive).
        const match = planText.match(/\{[\s\S]*\}$/);
        if (match) {
          try {
            parsedPlan = JSON.parse(match[0]);
          } catch {
            parsedPlan = null;
          }
        }
      }

      const repairedPlan = validateAndRepairStudyPlan(parsedPlan, ctx);
      setStudyPlan(repairedPlan);
      if (ctx.isAggressive) {
        setMessage(
          'Your timeline is aggressive for this score gap. Consider adding 2-3 hours per week or extending your timeframe.'
        );
        setMessageType('error');
      }
      try {
        localStorage.setItem(
          STUDY_PLAN_STORAGE_KEY,
          JSON.stringify({
            formData,
            studyPlan: repairedPlan,
            completedTasks: Array.from(completedTasks),
            scoreEnabled
          })
        );
      } catch {
        // ignore
      }

    } catch (error) {
      console.error('Error generating study plan:', error);
      setMessage('Error generating study plan. Please try again.');
      setMessageType('error');
    } finally {
      setIsGenerating(false);
    }
  };

  const toggleTaskCompletion = (weekIndex: number, taskIndex: number) => {
    const taskId = `week-${weekIndex}-task-${taskIndex}`;
    setCompletedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }

      // Persist completion state too, so checkmarks remain consistent.
      try {
        localStorage.setItem(
          STUDY_PLAN_STORAGE_KEY,
          JSON.stringify({
            formData,
            studyPlan,
              completedTasks: Array.from(newSet),
              scoreEnabled
          })
        );
      } catch {
        // ignore
      }

      return newSet;
    });
  };

  const renderScoreSlider = ({
    field,
    label,
    scoreType,
    optional
  }: {
    field: keyof StudyPlanData;
    label: string;
    scoreType: 'total' | 'section';
    optional?: boolean;
  }) => {
    const config = SCORE_CONFIG[formData.testType][scoreType];
    const value = getScoreValue(field, scoreType);
    const position = (value - config.min) / (config.max - config.min);
    const optionalField = field as OptionalScoreField;
    const enabled = !optional || scoreEnabled[optionalField];

    return (
      <div className={`score-slider-card ${!enabled ? 'muted' : ''}`}>
        <div className="score-slider-header">
          <label>{label}</label>
          {optional && (
            <label className="score-slider-toggle">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => handleScoreEnabledChange(optionalField, e.target.checked)}
              />
              <span>Include</span>
            </label>
          )}
        </div>
        <div
          className="score-slider-track-wrap"
          style={{ ['--slider-position' as string]: String(position) }}
        >
          <div className="score-slider-bubble" aria-hidden="true">
            {value}
          </div>
          <input
            type="range"
            className="score-slider-input"
            min={config.min}
            max={config.max}
            step={config.step}
            value={value}
            disabled={!enabled}
            onChange={(e) => {
              const next = parseInt(e.target.value, 10);
              handleInputChange(field, next);
              if (optional && !enabled) {
                handleScoreEnabledChange(optionalField, true);
              }
            }}
          />
          <div className="score-slider-scale">
            <span>{config.min}</span>
            <span>{Math.round((config.min + config.max) / 2)}</span>
            <span>{config.max}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="study-plan">
      <div className="study-plan-container">
        <div className="study-plan-header">
          <h1 className="study-plan-title">Study Planner</h1>
          <p className="study-plan-subtitle">Create your personalized GMAT or GRE test prep roadmap</p>
        </div>

        <div className="study-plan-form">
          {/* Test Type */}
          <div className="form-section">
            <h2 className="section-title">Test Information</h2>
            <div className="study-test-type-toggle" role="tablist" aria-label="Select test type">
              <button
                type="button"
                role="tab"
                aria-selected={formData.testType === 'GMAT_LEGACY'}
                className={`study-test-type-button ${formData.testType === 'GMAT_LEGACY' ? 'active' : ''}`}
                onClick={() => handleTestTypeChange('GMAT_LEGACY')}
              >
                GMAT Legacy
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={formData.testType === 'GMAT_FOCUS'}
                className={`study-test-type-button ${formData.testType === 'GMAT_FOCUS' ? 'active' : ''}`}
                onClick={() => handleTestTypeChange('GMAT_FOCUS')}
              >
                GMAT Focus
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={formData.testType === 'GRE'}
                className={`study-test-type-button ${formData.testType === 'GRE' ? 'active' : ''}`}
                onClick={() => handleTestTypeChange('GRE')}
              >
                GRE
              </button>
            </div>

            <div className="study-score-sliders">
              {renderScoreSlider({
                field: 'currentScore',
                label: 'Current Total (Optional)',
                scoreType: 'total',
                optional: true
              })}
              {renderScoreSlider({
                field: 'targetScore',
                label: 'Target Total',
                scoreType: 'total'
              })}
              {renderScoreSlider({
                field: 'currentQuantScore',
                label: 'Current Quant (Optional)',
                scoreType: 'section',
                optional: true
              })}
              {renderScoreSlider({
                field: 'targetQuantScore',
                label: 'Target Quant (Optional)',
                scoreType: 'section',
                optional: true
              })}
              {renderScoreSlider({
                field: 'currentVerbalScore',
                label: 'Current Verbal (Optional)',
                scoreType: 'section',
                optional: true
              })}
              {renderScoreSlider({
                field: 'targetVerbalScore',
                label: 'Target Verbal (Optional)',
                scoreType: 'section',
                optional: true
              })}
            </div>
          </div>

          {/* Timeline */}
          <div className="form-section">
            <h2 className="section-title">Study Timeline</h2>
            <div className="form-grid form-grid-study-timeline">
              <div className="form-group study-plan-test-date-group">
                <label htmlFor="testDate">Test Date (Optional)</label>
                <input
                  type="date"
                  id="testDate"
                  value={formData.testDate || ''}
                  onChange={(e) => handleInputChange('testDate', e.target.value)}
                />
              </div>
              <fieldset className="form-group study-plan-segment-fieldset">
                <legend>Study Timeframe (Months)</legend>
                <div className="study-plan-segment-picker">
                  {STUDY_TIMEFRAME_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`study-plan-segment-button ${
                        formData.studyTimeframe === value ? 'active' : ''
                      }`}
                      aria-label={studyTimeframeAriaLabel(value)}
                      aria-pressed={formData.studyTimeframe === value}
                      onClick={() => handleInputChange('studyTimeframe', value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </fieldset>
              <fieldset className="form-group study-plan-segment-fieldset">
                <legend>Hours per Week</legend>
                <div className="study-plan-segment-picker">
                  {HOURS_PER_WEEK_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`study-plan-segment-button ${
                        formData.hoursPerWeek === value ? 'active' : ''
                      }`}
                      aria-label={hoursPerWeekAriaLabel(value)}
                      aria-pressed={formData.hoursPerWeek === value}
                      onClick={() => handleInputChange('hoursPerWeek', value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </fieldset>
            </div>
          </div>

          {/* Save + Generate */}
          <div className="form-actions">
            <button
              type="button"
              className="save-button"
              onClick={() => void saveStudyPlan()}
              disabled={isSaving || isGenerating}
            >
              {isSaving ? 'Saving...' : 'Save Plan'}
            </button>
            <button
              type="button"
              className="generate-button"
              onClick={generateStudyPlan}
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating Plan...' : 'Generate Study Plan'}
            </button>
          </div>

          {message && (
            <div className={`study-plan-message ${messageType}`}>
              {message}
            </div>
          )}
        </div>

        {/* Study Plan Results */}
        {studyPlan && (
          <div className="study-plan-results">
            <h2 className="results-title">Your Personalized Study Plan</h2>
            
            {/* Overview */}
            <div className="plan-section overview">
              <h3>Plan Overview</h3>
              <p>{studyPlan.overview}</p>
            </div>

            {/* Weekly Plan */}
            <div className="plan-section weekly-plan">
              <h3>Week-by-Week Breakdown</h3>
              <div className="weekly-timeline">
                {studyPlan.weeklyPlan.map((week, weekIndex) => (
                  <div key={weekIndex} className="week-card">
                    <div className="week-header">
                      <h4>Week {week.week}</h4>
                      <span className="week-focus">{week.focus}</span>
                    </div>
                    <div className="week-tasks">
                      <h5>Tasks:</h5>
                      <ul>
                        {week.tasks.map((task, taskIndex) => (
                          <li key={taskIndex} className="task-item">
                            <label className="task-checkbox">
                              <input
                                type="checkbox"
                                checked={completedTasks.has(`week-${weekIndex}-task-${taskIndex}`)}
                                onChange={() => toggleTaskCompletion(weekIndex, taskIndex)}
                              />
                              <span className={completedTasks.has(`week-${weekIndex}-task-${taskIndex}`) ? 'completed' : ''}>
                                {stripLeadingEmoji(task)}
                              </span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {week.practiceTests && (
                      <div className="week-practice">
                        <h5>Practice Test:</h5>
                        <p>{stripLeadingEmoji(week.practiceTests)}</p>
                      </div>
                    )}
                    <div className="week-resources">
                      <h5>Resources:</h5>
                      <ul>
                        {week.resources.map((resource, index) => (
                          <li key={index}>{stripLeadingEmoji(resource)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Practice Test Schedule */}
            {studyPlan.practiceTestSchedule.length > 0 && (
              <div className="plan-section practice-schedule">
                <h3>Practice Test Schedule</h3>
                <div className="practice-timeline">
                  {studyPlan.practiceTestSchedule.map((test, index) => (
                    <div key={index} className="practice-test-card">
                      <div className="test-week">Week {test.week}</div>
                      <div className="test-type">{test.testType}</div>
                      <div className="test-focus">{test.focus}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Study Strategies */}
            {studyPlan.studyStrategies.length > 0 && (
              <div className="plan-section strategies">
                <h3>Study Strategies</h3>
                <ul>
                  {studyPlan.studyStrategies.map((strategy, index) => (
                    <li key={index} className="study-plan-icon-row">
                      <Sparkles className="study-plan-li-svg" aria-hidden focusable="false" />
                      <span>{stripLeadingEmoji(strategy)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Resource Recommendations */}
            {studyPlan.resourceRecommendations.length > 0 && (
              <div className="plan-section resources">
                <h3>Recommended Resources</h3>
                {studyPlan.resourceRecommendations.map((category, index) => (
                  <div key={index} className="resource-category">
                    <h4>{category.category}</h4>
                    <ul>
                      {category.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="study-plan-icon-row">
                          <StudyPlanResourceLeadIcon text={item} category={category.category} />
                          <span>{stripLeadingEmoji(item)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Milestones */}
            {studyPlan.milestones.length > 0 && (
              <div className="plan-section milestones">
                <h3>Progress Milestones</h3>
                <div className="milestones-timeline">
                  {studyPlan.milestones.map((milestone, index) => (
                    <div key={index} className="milestone-card">
                      <div className="milestone-week">Week {milestone.week}</div>
                      <div className="milestone-title">{stripLeadingEmoji(milestone.milestone)}</div>
                      <div className="milestone-description">{stripLeadingEmoji(milestone.description)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyPlan;
